import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { type FieldValidatorType } from '@progress/kendo-vue-form'
import { GetBeeNumberAuthenicatedUser } from '../stores/SettingsStore'
import { GetConsultantFromLocalStorageOrApi } from '../services/ConsultantService'

/**
 * Stops a consultant from entering their own email address as a customer's, guest's or
 * contact's email. Contacts auto-link to whatever business entity owns the email, so reusing
 * the consultant's own address files the contact against the consultant and locks it as class
 * CONSULTANT, which the UI then refuses to let them edit.
 *
 * These screens are the only way a consultant creates a contact, so this is the only guard -
 * the API does not reject the address separately.
 */
export function useConsultantOwnEmailGuard() {
  const { t } = useI18n()
  const logger = useLogger()

  const consultantEmailAddress = ref<string | null>(null)

  async function loadConsultantEmailAddress() {
    try {
      const beeNumber = await GetBeeNumberAuthenicatedUser()
      if (!beeNumber) return

      const consultant = await GetConsultantFromLocalStorageOrApi(beeNumber)
      consultantEmailAddress.value = consultant?.emailAddress ?? null
    } catch (error) {
      // Without the consultant's email the guard simply stays out of the way; the API still rejects.
      logger.info('useConsultantOwnEmailGuard: could not resolve the consultant email address', error)
      consultantEmailAddress.value = null
    }
  }

  function isConsultantOwnEmail(value?: string | null): boolean {
    if (!value || !consultantEmailAddress.value) return false
    return value.trim().toLowerCase() === consultantEmailAddress.value.trim().toLowerCase()
  }

  function createConsultantOwnEmailValidator(): FieldValidatorType {
    return (value: string) =>
      isConsultantOwnEmail(value) ? t('validators.consultantOwnEmailNotAllowed') : ''
  }

  return {
    consultantEmailAddress,
    loadConsultantEmailAddress,
    isConsultantOwnEmail,
    createConsultantOwnEmailValidator,
  }
}
